import { z } from 'zod';
import { pageQuerySchema } from './marketplace.js';
/**
 * Reporting someone, and raising an alarm.
 * [Parent step 17, Job Poster step 25]
 *
 * Two different things, kept apart on purpose. A report is a complaint about a
 * person and goes to moderation. An alert is "something is wrong right now" —
 * it is about a situation, it carries a severity, and nothing about it should
 * require the person raising it to explain themselves first.
 */
/* -------------------------------------------------------------------------- */
/* Reports                                                                    */
/* -------------------------------------------------------------------------- */
export const reportStatusSchema = z.enum(['OPEN', 'UNDER_REVIEW', 'RESOLVED', 'DISMISSED']);
export const reportSchema = z.object({
    id: z.string(),
    status: reportStatusSchema,
    reason: z.string(),
    /** Who was reported, as a name — never their contact details. */
    reportedName: z.string(),
    jobTitle: z.string().nullable(),
    createdAt: z.string(),
    reviewedAt: z.string().nullable(),
});
export const createReportInputSchema = z.object({
    /** Any publicId that identifies the person: teen, parent, poster or account. */
    reportedPersonId: z.string().uuid(),
    /** The job it happened on, when there is one. */
    jobId: z.string().uuid().optional(),
    reason: z
        .string()
        .trim()
        .min(10, 'Tell us what happened — a sentence is enough')
        .max(4000),
});
export const reportListQuerySchema = pageQuerySchema.extend({
    status: reportStatusSchema.optional(),
});
/* -------------------------------------------------------------------------- */
/* Safety alerts                                                              */
/* -------------------------------------------------------------------------- */
export const alertSeveritySchema = z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']);
export const safetyAlertStatusSchema = z.enum(['OPEN', 'ACTIONED', 'CLOSED']);
/**
 * The kinds of alarm the product understands.
 *
 * Deliberately a short, plain list rather than free text: someone raising one
 * of these is usually in a hurry, and picking from four options is faster than
 * describing a category.
 */
export const alertTypeSchema = z.enum([
    'FEELING_UNSAFE',
    'DID_NOT_ARRIVE',
    'LOST_CONTACT',
    'INAPPROPRIATE_BEHAVIOUR',
    'OTHER',
]);
export const ALERT_TYPE_LABEL = {
    FEELING_UNSAFE: 'Someone feels unsafe',
    DID_NOT_ARRIVE: 'They did not arrive',
    LOST_CONTACT: 'I cannot reach them',
    INAPPROPRIATE_BEHAVIOUR: 'Inappropriate behaviour',
    OTHER: 'Something else',
};
export const safetyAlertSchema = z.object({
    id: z.string(),
    alertType: z.string(),
    severity: alertSeveritySchema,
    status: safetyAlertStatusSchema,
    jobTitle: z.string().nullable(),
    createdAt: z.string(),
});
export const raiseAlertInputSchema = z.object({
    alertType: alertTypeSchema,
    severity: alertSeveritySchema.default('HIGH'),
    /** The job it concerns, when the alarm is about a specific piece of work. */
    jobId: z.string().uuid().optional(),
    /** A guardian raising this on behalf of a teen they are approved for. */
    aboutTeenId: z.string().uuid().optional(),
});
export const alertListQuerySchema = pageQuerySchema.extend({
    status: safetyAlertStatusSchema.optional(),
});
//# sourceMappingURL=safety.js.map